import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

interface OverviewCardProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  minHeight?: number;
  contentSx?: SxProps<Theme>;
}

/**
 * Shared visual contract for Attendance Overview panels.
 *
 * Every panel gets the same border, fixed header baseline, body padding and
 * flex behaviour. Side-by-side cards therefore stretch to the same height
 * without relying on MUI Grid's negative-margin layout.
 */
const OverviewCard: React.FC<OverviewCardProps> = ({
  title,
  subtitle,
  action,
  children,
  minHeight = 0,
  contentSx,
}) => (
  <Paper
    elevation={0}
    sx={{
      width: '100%',
      height: '100%',
      minWidth: 0,
      minHeight,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 'none',
    }}
  >
    <Box
      sx={{
        minHeight: 64,
        px: 2.5,
        py: 1.25,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 1, md: 2 },
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.35 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
    </Box>
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        px: 2.5,
        py: 1.5,
        ...(contentSx as object),
      }}
    >
      {children}
    </Box>
  </Paper>
);

interface OverviewEmptyStateProps {
  title: React.ReactNode;
  hint?: React.ReactNode;
}

export const OverviewEmptyState: React.FC<OverviewEmptyStateProps> = ({ title, hint }) => (
  <Box
    sx={{
      minHeight: 156,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      px: 2,
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
      {title}
    </Typography>
    {hint ? (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, maxWidth: 320, lineHeight: 1.45 }}>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

export default OverviewCard;
